import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/projects/components/ui/dialog";
import { Button } from "@/projects/components/ui/button";
import { Label } from "@/projects/components/ui/label";
import { Textarea } from "@/projects/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import { fetchTeachers } from "@/projects/lib/teachersApi";
import {
  assignStudentVideoSubmission,
  getSubmissionApiErrorMessage,
} from "@/projects/lib/submissionsApi";
import type { Submission, Teacher } from "@/projects/lib/store";
import { toast } from "@/projects/lib/toast";
import { StatusBadge } from "@/projects/components/status-badge";
import { Loader2 } from "lucide-react";

export function AssignTeacherModal({
  submissions: selectedSubs,
  open,
  onClose,
  onAssigned,
}: {
  /** Submissions selected for assign (from live API list). */
  submissions: Submission[] | null;
  open: boolean;
  onClose: () => void;
  /** Called after a successful assign API call. */
  onAssigned?: (
    teacherId: string,
    notes: string,
    submissionIds: string[]
  ) => void;
}) {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teacherId, setTeacherId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const subs = useMemo(() => selectedSubs ?? [], [selectedSubs]);
  const isBulk = subs.length > 1;
  const single = subs[0];
  const activeTeachers = teachers.filter(t => t.active);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setLoadingTeachers(true);
    setTeacherId("");
    setNotes("");

    void (async () => {
      try {
        const rows = await fetchTeachers();
        if (!cancelled) {
          setTeachers(rows);
        }
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string; detail?: string } };
          message?: string;
        };
        if (!cancelled) {
          toast.error(
            err?.response?.data?.message ||
              err?.response?.data?.detail ||
              err?.message ||
              "Failed to load teachers."
          );
          setTeachers([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTeachers(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleAssign = async () => {
    if (!teacherId) {
      toast.error("Please select a teacher.");
      return;
    }
    if (!subs.length) {
      toast.error("No submission selected.");
      return;
    }

    const selectedTeacher = teachers.find(t => t.id === teacherId);
    const assignedTeacherUserId = selectedTeacher?.userId;
    if (!assignedTeacherUserId) {
      toast.error("Selected teacher has no user id.");
      return;
    }

    setSaving(true);
    try {
      for (const sub of subs) {
        const submissionId = sub.apiId || sub.id;
        await assignStudentVideoSubmission(
          submissionId,
          assignedTeacherUserId,
          notes
        );
      }
      onAssigned?.(
        assignedTeacherUserId,
        notes,
        subs.map(s => s.id)
      );
      toast.success(
        isBulk
          ? `Assigned ${subs.length} submissions successfully`
          : "Teacher assigned successfully"
      );
      setTeacherId("");
      setNotes("");
      onClose();
    } catch (error: unknown) {
      toast.error(
        getSubmissionApiErrorMessage(error, "Failed to assign teacher.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isBulk
              ? `Assign Teacher to ${subs.length} Submissions`
              : "Assign Teacher"}
          </DialogTitle>
          <DialogDescription>
            {isBulk
              ? "Select a teacher to assign all selected submissions."
              : "Choose a teacher to verify this student's submission."}
          </DialogDescription>
        </DialogHeader>

        {!isBulk && single && (
          <div className="bg-muted/40 space-y-1 rounded-lg border p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Student</span>
              <span className="font-medium">
                {single.studentName} · {single.studentId}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Video Title</span>
              <span className="font-medium">{single.videoTitle}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Submitted</span>
              <span>{new Date(single.submittedAt).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Current Status</span>
              <StatusBadge status={single.status} />
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>Select Teacher</Label>
            <Select
              value={teacherId}
              onValueChange={setTeacherId}
              disabled={loadingTeachers || saving}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    loadingTeachers ? "Loading teachers…" : "Choose a teacher"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {loadingTeachers && (
                  <div className="text-muted-foreground flex items-center gap-2 px-2 py-3 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                  </div>
                )}
                {!loadingTeachers && activeTeachers.length === 0 && (
                  <div className="text-muted-foreground px-2 py-3 text-sm">
                    No active teachers found.
                  </div>
                )}
                {activeTeachers.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex w-full items-center gap-2">
                      <span className="font-medium">{t.name}</span>
                      {(t.subject || t.email) && (
                        <span className="text-muted-foreground text-xs">
                          · {[t.subject, t.email].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assignment Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any note for the assigned teacher"
              rows={3}
              disabled={saving}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleAssign()}
            disabled={saving || loadingTeachers || !teacherId}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isBulk ? `Assign ${subs.length}` : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
